import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CalendarDays,
  Eye,
  Handshake,
  RefreshCw,
  UserRound,
  Users,
} from "lucide-react";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";
import Icon from "@/components/Icon";
import Modal from "@/components/Modal";
import { GetBusinessDashboardStatsAPI } from "@/services/api/business/getBusinessDashboardStats";
import { openChatRoom } from "@/services/middleware/chatHelper";
import {
  useBusinessSponsors,
  type BusinessSponsor,
  type SponsorCompletionGroup,
  type SponsorTaskCompletion,
} from "@/hooks/useBusinessSponsors";
import { formatCouponDate } from "@/utils/businessCoupon";
import { getActivityDeadlineString } from "@/utils/deadline";
import {
  getEffectiveJobStatus,
  getSponsorProofIgAccounts,
  getSponsorProofMedia,
  getSponsorProofRegisteredUsernames,
} from "@/utils/sponsorStatus";

type Tab = "sponsors" | "review";

/** Task types the AI bot verifies, so no manual review is offered. */
const BOT_CHECKED_TASK_TYPES = ["register_app", "ensure_followers"];

const DEFAULT_IG_ACCOUNTS = ["@uninfohk", "@we2tech", "@care.furry.kids"];
const DEFAULT_STUDENT_COUNT = "40";

/**
 * Task titles are stored as data, so they are assembled from these fragments —
 * the same English text is used when building them on the web and on mobile.
 */
const FOLLOW_TASK_PREFIX =
  "Both the official society Instagram account and each committee account must follow the accounts (";
const FOLLOW_TASK_SUFFIX =
  ") and like all posts for 1 year. If already done, recruit others to fulfill.";
const FOLLOWER_TASK_PREFIX = "Ensure ";
const REGISTER_TASK_TITLE =
  "Both the official society Instagram account and each committee account must download and register for the Uninfo HK app. If already registered, recruit another student who has not registered to do so.";
const STORY_TASK_TITLE =
  "Publish one Story and Post, tagging @uninfohk and @we2tech, expressing gratitude to the Sponsor Provider and promoting the Uninfo HK platform. The official account Post is subject to prior written approval by the Sponsor Provider and must remain published and visible for at least one (1) year to ensure continuous compliance with this obligation.";

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Lifetime reach & engagement for one sponsor, from the dashboard stats API. */
type SponsorStats = {
  reads?: number;
  readers?: number;
  redeemed?: number;
  students?: number;
};

type ProofMedia = { type: string; url: string; comment?: string };

/**
 * Normalise the proof shapes the backend stores into renderable rows.
 *
 * Delegates to the shared proof-shape helpers. This used to sniff `media` and
 * `ig_accounts` by hand, so a `register_app` proof (`registered_usernames`)
 * rendered as no rows at all and the sponsor saw an empty submission.
 */
const getProofMedia = (proof: any): ProofMedia[] => {
  const toRow = (type: string) => (value: string) => ({ type, url: value });
  const media = getSponsorProofMedia(proof)
    .filter((item: any) => item && item.url)
    .map((item: any) =>
      typeof item === "string" ? { type: "image", url: item } : item,
    );
  if (media.length > 0) return media;
  return [
    ...getSponsorProofIgAccounts(proof).map(toRow("ig_account")),
    ...getSponsorProofRegisteredUsernames(proof).map(toRow("username")),
  ];
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  completed: "Completed",
  failed: "Failed",
  ready: "Awaiting check",
  processing: "Reviewing",
};

/** The hook groups by sponsor + student; the page regroups that by student. */
type StudentCompletionGroup = {
  student_id: string;
  student_name?: string | null;
  groups: SponsorCompletionGroup[];
};

/**
 * Business sponsor centre content — the web port of the RN
 * `BusinessSponsorManageScreen`: the sponsors assigned to the business and the
 * task completions their students submitted (read-only; UninfoHK reviews).
 */
const BusinessSponsorTasksContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    sponsors,
    sponsorTotal,
    sponsorsLoading,
    sponsorsRefreshing,
    hasMoreSponsors,
    loadMoreSponsors,
    refreshSponsors,
    groups,
    completionsLoading,
    completionsRefreshing,
    hasMoreCompletions,
    loadMoreCompletions,
    refreshCompletions,
  } = useBusinessSponsors();

  const [tab, setTab] = useState<Tab>("sponsors");
  const [previewUrl, setPreviewUrl] = useState("");
  const [detailGroupKey, setDetailGroupKey] = useState<string | null>(null);
  const [statsMap, setStatsMap] = useState<Map<string, SponsorStats>>(
    new Map(),
  );

  useEffect(() => {
    const loadStats = async () => {
      const resp = await GetBusinessDashboardStatsAPI({});
      if (resp?.status !== 0) {
        setStatsMap(new Map());
        return;
      }
      const map = new Map<string, SponsorStats>();
      for (const item of resp.result?.sponsors ?? []) {
        if (!item.sponsor_id) continue;
        map.set(String(item.sponsor_id), {
          reads: item.reads,
          readers: item.readers,
          redeemed: item.redeemed,
          students: item.students,
        });
      }
      setStatsMap(map);
    };
    loadStats();
  }, []);

  const refreshAll = () => {
    refreshSponsors();
    refreshCompletions();
  };

  const pendingCount = useMemo(
    () =>
      groups.reduce(
        (sum, group) =>
          sum +
          (group.sponsor_task_completions ?? []).filter(
            (completion) => getEffectiveJobStatus(completion) === "pending",
          ).length,
        0,
      ),
    [groups],
  );

  const studentGroups = useMemo(() => {
    const map = new Map<string, StudentCompletionGroup>();
    for (const group of groups) {
      const key = group.student_id || group.student_name || "unknown";
      const entry = map.get(key) ?? {
        student_id: group.student_id ?? key,
        student_name: group.student_name,
        groups: [],
      };
      entry.groups.push(group);
      map.set(key, entry);
    }
    return [...map.values()];
  }, [groups]);

  /**
   * Looked up from `groups` on every render (not stored), so a review that
   * patches a completion in place keeps the open detail modal in sync.
   */
  const detailGroup = detailGroupKey
    ? (groups.find(
        (group) => `${group.sponsor_id}|${group.student_id}` === detailGroupKey,
      ) ?? null)
    : null;
  const detailCompletions = detailGroup?.sponsor_task_completions ?? [];
  const detailDeadlineStr = getActivityDeadlineString(
    detailGroup?.sponsor_activity?.activity_date,
  );

  const renderSponsorCard = (sponsor: BusinessSponsor) => {
    const tasks = sponsor.tasks ?? [];
    const stats = statsMap.get(sponsor.id);
    return (
      <article
        key={sponsor.id}
        className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
      >
        <div className="flex items-center gap-3 p-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50">
            {sponsor.image_url ? (
              <img
                src={sponsor.image_url}
                alt={sponsor.name ?? t("Sponsor")}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <Handshake size={24} className="text-gray-300" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-2 text-sm font-bold leading-5 text-gray-900">
              {sponsor.name || t("No Sponsor Name")}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("Sponsor Tasks")}: {tasks.length}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400">
              {formatCouponDate(sponsor.ctime, i18n?.language)}
            </p>
          </div>
        </div>

        {sponsor.description && (
          <p className="line-clamp-2 px-4 text-xs leading-4 text-gray-500">
            {sponsor.description}
          </p>
        )}

        {tasks.length > 0 && (
          <ul className="mt-2 space-y-1 px-4">
            {tasks.slice(0, 3).map((task) => (
              <li key={task.id} className="truncate text-[11px] text-gray-400">
                · {task.title}
              </li>
            ))}
            {tasks.length > 3 && (
              <li className="text-[11px] font-semibold text-[#ffa217]">
                +{tasks.length - 3}
              </li>
            )}
          </ul>
        )}

        {stats ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Eye size={12} className="text-gray-400" />
              {t("Reads")}: {toCount(stats.reads)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Handshake size={12} className="text-gray-400" />
              {t("redeemed")}: {toCount(stats.redeemed)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users size={12} className="text-gray-400" />
              {t("Students")}: {toCount(stats.students)}
            </span>
          </div>
        ) : null}

      </article>
    );
  };

  const renderCompletionRow = (completion: SponsorTaskCompletion) => {
    const status = getEffectiveJobStatus(completion);
    const statusKey = STATUS_LABEL_KEYS[status] ?? "Pending";
    const statusClass =
      status === "completed"
        ? "bg-green-50 text-green-600"
        : status === "failed"
          ? "bg-red-50 text-red-500"
          : "bg-orange-50 text-[#ffa217]";
    const media = getProofMedia(completion.proof);
    const isBotChecked =
      typeof completion.type === "string" &&
      BOT_CHECKED_TASK_TYPES.includes(completion.type);

    return (
      <li
        key={completion.id}
        className="rounded-xl border border-gray-100 bg-gray-50/60 p-3"
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs font-semibold leading-4 text-gray-800">
            {completion.task_title || t("Task")}
          </p>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${statusClass}`}
          >
            {t(statusKey)}
          </span>
        </div>

        {typeof completion.review_count === "number" &&
          completion.review_count > 0 && (
            <p className="mt-1 text-[10px] text-gray-400">
              {t("Review count: {{count}}", {
                count: completion.review_count,
              })}
            </p>
          )}

        {completion.type === "publish_story_post" &&
          detailGroup?.sponsor_activity?.proof_code && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
              <p className="text-[11px] font-bold text-gray-800">
                {t("Proof Code")}:{" "}
                <span className="tracking-[0.15em] text-[#ffa217]">
                  {detailGroup.sponsor_activity.proof_code}
                </span>
              </p>
              <p className="mt-1 text-[10px] font-semibold text-red-600">
                {t(
                  "Reject if the proof code is not clearly visible in the Story/Post.",
                )}
              </p>
              {completion.proof?.post_url && (
                <a
                  href={completion.proof.post_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-[10px] font-semibold text-blue-600 underline"
                >
                  {completion.proof.post_url}
                </a>
              )}
            </div>
          )}

        {media.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {media.map((item, index) =>
              item.type === "video" ? (
                <a
                  key={`${item.url}-${index}`}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-600"
                >
                  {t("Upload Video")}
                </a>
              ) : item.type === "ig_account" ? (
                <span
                  key={`${item.url}-${index}`}
                  className="rounded-lg bg-white px-2 py-1 text-[10px] font-semibold text-gray-600"
                >
                  @{String(item.url).replace(/^@/, "")}
                </span>
              ) : (
                <button
                  key={`${item.url}-${index}`}
                  type="button"
                  onClick={() => setPreviewUrl(item.url)}
                  className="h-14 w-14 overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  <img
                    src={item.url}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
              ),
            )}
          </div>
        )}

        {isBotChecked && (
          <p className="mt-2 text-[10px] text-gray-400">
            {t(
              "This task will be automatically checked by our AI bot. No manual approval is needed.",
            )}
          </p>
        )}


      </li>
    );
  };

  /**
   * One sponsor redeemed by one student, nested under its student card: a
   * compact summary row (mirroring the RN `BusinessSponsorManageScreen` card)
   * that opens the per-task review list in the detail modal.
   */
  const renderSponsorSection = (group: SponsorCompletionGroup) => {
    const completions = group.sponsor_task_completions ?? [];
    const completed = completions.filter(
      (completion) => getEffectiveJobStatus(completion) === "completed",
    ).length;
    const deadlineStr = getActivityDeadlineString(
      group.sponsor_activity?.activity_date,
    );
    return (
      <button
        type="button"
        key={`${group.sponsor_id}-${group.student_id}`}
        onClick={() =>
          setDetailGroupKey(`${group.sponsor_id}|${group.student_id}`)
        }
        className="w-full rounded-xl border border-gray-100 bg-gray-50/60 p-3 text-left transition hover:border-[#ffa217]"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
            {group.sponsor?.image_url ? (
              <img
                src={group.sponsor.image_url}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <Handshake size={18} className="text-gray-300" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-sm font-bold text-gray-900">
              {group.sponsor?.name || t("No Sponsor Name")}
            </h4>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
              {group.sponsor_activity?.activity_name && (
                <span>
                  {t("Activity: {{name}}", {
                    name: group.sponsor_activity.activity_name,
                  })}
                </span>
              )}
              {group.sponsor_activity?.activity_date && (
                <span className="inline-flex items-center gap-1">
                  <CalendarDays size={11} />
                  {group.sponsor_activity.activity_date}
                </span>
              )}
            </div>
          </div>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
              completed === completions.length
                ? "bg-green-50 text-green-600"
                : "bg-[#fff3e0] text-[#ffa217]"
            }`}
          >
            {t("Sponsor Tasks: {{completed}}/{{total}} Completed", {
              completed,
              total: completions.length,
            })}
          </span>
        </div>

        {deadlineStr && (
          <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-red-500">
            <CalendarClock size={11} />
            {t("Deadline")}: {deadlineStr}
          </p>
        )}
      </button>
    );
  };

  /** One card per student, so the business sees who redeemed their sponsors. */
  const renderStudentCard = (student: StudentCompletionGroup) => {
    const initial = (student.student_name || "").trim().charAt(0);
    return (
      <article
        key={student.student_id}
        className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-50 text-sm font-bold text-gray-400">
            {initial || <UserRound size={18} className="text-gray-300" />}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-bold text-gray-900">
              {student.student_name || "—"}
            </h3>
            <p className="truncate text-xs text-gray-500">
              {student.groups.length} {t("Sponsors")}
            </p>
          </div>
        </div>

        <div className="mt-3 space-y-2">
          {student.groups.map(renderSponsorSection)}
        </div>
      </article>
    );
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("Sponsor Tasks")}
        </h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={tab === "sponsors" ? refreshSponsors : refreshCompletions}
            disabled={
              tab === "sponsors" ? sponsorsRefreshing : completionsRefreshing
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-60"
          >
            <RefreshCw
              size={14}
              className={
                (
                  tab === "sponsors"
                    ? sponsorsRefreshing
                    : completionsRefreshing
                )
                  ? "animate-spin"
                  : ""
              }
            />
            {t("Refresh")}
          </button>

        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 rounded-xl border border-gray-100 bg-white p-1">
        {(
          [
            { key: "sponsors", label: "Sponsors" },
            { key: "review", label: "Task Completions" },
          ] as const
        ).map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition ${
              tab === item.key
                ? "bg-[#fff3e0] text-[#ffa217]"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(item.label)}
            {item.key === "review" && pendingCount > 0 && (
              <span className="ml-1.5 rounded-full bg-[#ffa217] px-1.5 py-0.5 text-[10px] font-bold text-white">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "sponsors" ? (
        <>
          {sponsorsLoading ? (
            <AdminListSkeleton count={3} />
          ) : sponsors.length === 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white">
              <AdminEmptyState
                iconType="MaterialCommunityIcons"
                iconName="handshake-outline"
                title={t("No sponsors")}
                message={t("Sponsors are set up by UninfoHK.")}
              />
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {sponsors.map(renderSponsorCard)}
              </div>
              {hasMoreSponsors && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMoreSponsors}
                    className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
                  >
                    {t("Load more")}
                  </button>
                </div>
              )}
              {sponsorTotal > sponsors.length && !hasMoreSponsors && (
                <p className="mt-4 text-center text-xs text-gray-400">
                  {sponsors.length}/{sponsorTotal}
                </p>
              )}
            </>
          )}
        </>
      ) : completionsLoading ? (
        <AdminListSkeleton count={3} />
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="MaterialCommunityIcons"
            iconName="clipboard-check-outline"
            title={t("No sponsor tasks found.")}
            message={t("Failed to load sponsor completions.")}
            actionLabel={t("Refresh")}
            onAction={refreshCompletions}
          />
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {studentGroups.map(renderStudentCard)}
          </div>
          {hasMoreCompletions && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={loadMoreCompletions}
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217]"
              >
                {t("Load more")}
              </button>
            </div>
          )}
        </>
      )}





      <Modal
        visible={Boolean(detailGroup)}
        onClose={() => setDetailGroupKey(null)}
      >
        <div className="max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-5 shadow-lg">
          <h2 className="text-base font-bold text-gray-900">
            {detailGroup?.sponsor?.name || t("No Sponsor Name")}
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            {t("Redeemed By {{name}}", {
              name: detailGroup?.student_name || "—",
            })}
          </p>

          {detailGroup?.sponsor_activity?.activity_name && (
            <p className="mt-2 text-xs font-semibold text-gray-700">
              {t("Activity: {{name}}", {
                name: detailGroup.sponsor_activity.activity_name,
              })}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
            {detailGroup?.sponsor_activity?.activity_date && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays size={11} />
                {detailGroup.sponsor_activity.activity_date}
              </span>
            )}
            {detailDeadlineStr && (
              <span className="inline-flex items-center gap-1 font-semibold text-red-500">
                <CalendarClock size={11} />
                {t("Deadline")}: {detailDeadlineStr}
              </span>
            )}
          </div>

          <ul className="mt-3 space-y-2">
            {detailCompletions.map((completion) =>
              renderCompletionRow(completion),
            )}
          </ul>
        </div>
      </Modal>



      <Modal visible={Boolean(previewUrl)} onClose={() => setPreviewUrl("")}>
        <div className="rounded-2xl bg-white p-4 shadow-lg">
          {previewUrl && (
            <img
              src={previewUrl}
              alt=""
              className="max-h-[70vh] w-full rounded-xl object-contain"
            />
          )}
          <button
            type="button"
            onClick={() => setPreviewUrl("")}
            className="mt-3 w-full rounded-xl bg-[#ffa217] py-2.5 text-sm font-bold text-white"
          >
            {t("Close")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * Sponsor centre gate: sponsor management is reserved for UninfoHK staff. The
 * read-only content stays visible underneath, faded behind a gradient overlay
 * with a centred lock card — mirroring the public site's login-gated panels.
 */
const BusinessSponsorTasksPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user_auth } = useSelector((state: any) => state.auth);

  const handleChatWithUninfoHK = async () => {
    const room = await openChatRoom({ type: "admin" });
    if (room?.id) navigate(`/portal/chat/${room.id}`);
  };

  if (user_auth === "admin") {
    return <BusinessSponsorTasksContent />;
  }

  return (
    <div className="relative min-h-[70vh] overflow-hidden">
      <BusinessSponsorTasksContent />
      <div className="absolute inset-0 flex flex-col items-center justify-end bg-gradient-to-b from-transparent via-white/70 to-white pb-10">
        <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-lg">
          <div className="mb-3 flex justify-center">
            <Icon
              iconType="MaterialCommunityIcons"
              name="lock-outline"
              size={40}
              color="#ffa217"
            />
          </div>
          <h3 className="mb-2 text-lg font-bold text-gray-900">
            {t("Sponsor the community with UninfoHK")}
          </h3>
          <p className="mb-4 text-sm text-gray-500">
            {t(
              "Chat with UninfoHK to learn how to sponsor the community and promote your brand.",
            )}
          </p>
          <button
            type="button"
            onClick={handleChatWithUninfoHK}
            className="w-full rounded-xl bg-[#ffa217] px-6 py-3 font-semibold text-white shadow-md transition hover:opacity-90"
          >
            {t("Chat with UninfoHK")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessSponsorTasksPage;